import React from "react";
import { Link } from "react-router-dom";
import { useTheme } from "@mui/material/styles";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import TimelineOutlinedIcon from "@mui/icons-material/TimelineOutlined";
import DnsOutlinedIcon from "@mui/icons-material/DnsOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import QueryStatsOutlinedIcon from "@mui/icons-material/QueryStatsOutlined";

import PublicPageLayout from "./PublicPageLayout";
import { getPublicSiteSx } from "./publicSiteStyles";
import { ANTI_CHEAT_PAGE_DESCRIPTION } from "./siteData";

const collectionCards = [
  {
    title: "Collected on every tick",
    body:
      "Ouro captures live gameplay context on every tick so suspicious behavior is analyzed as it unfolds, not only after a match ends.",
    icon: <TimelineOutlinedIcon />,
  },
  {
    title: "Server-side events",
    body:
      "The system watches real server events rather than relying on clips, accusations, or what a player claims happened.",
    icon: <DnsOutlinedIcon />,
  },
  {
    title: "Angles, timing, and visibility",
    body:
      "Signals like angles, visibility, shots, kills, movement, and timing are evaluated together so single moments are never the whole story.",
    icon: <VisibilityOutlinedIcon />,
  },
  {
    title: "Pattern-based evidence",
    body:
      "Ouro looks for repeated patterns over time, building decisions from broad context instead of reacting to one strange highlight.",
    icon: <QueryStatsOutlinedIcon />,
  },
];

const actionSteps = [
  {
    title: "Live events are collected",
    body:
      "Gameplay data arrives continuously from the server throughout the match.",
  },
  {
    title: "Suspicious patterns are flagged",
    body:
      "The system compares those events against representative cheating patterns and broader private checks that are not publicly listed.",
  },
  {
    title: "Context is reviewed",
    body:
      "Behavior is evaluated with the full sequence of what happened before, during, and after the engagement.",
  },
  {
    title: "Action follows evidence",
    body:
      "Decisions are made when the evidence is strong enough, with confidence built from accumulated server-side context.",
  },
];

const faqItems = [
  {
    question: "Does the anti-cheat run during live matches?",
    answer:
      "Yes. Ouro collects server-side gameplay data live during the match on every tick.",
  },
  {
    question: "Is this only based on reports or clips?",
    answer:
      "No. Reports can help direct attention, but the anti-cheat itself is built around server-side event collection and pattern analysis.",
  },
  {
    question: "Do you publish every detection pattern you use?",
    answer:
      "No. We name representative categories so players understand the system, but the full pattern library and decision logic stay private because publishing them would help cheaters adapt.",
  },
  {
    question: "Does the system stay static over time?",
    answer:
      "No. Ouro continuously adds and tests new patterns as more gameplay data flows through the system and the baseline for legitimate play gets stronger.",
  },
  {
    question: "Is Ouro adding a client-side anti-cheat?",
    answer:
      "Yes — a complementary client-side layer is in active development and is on the near-term roadmap. It is designed to cover specific things server-side analysis structurally cannot see, while server-side detection and identity-backed consequences remain the foundation. We are not betting the platform on a kernel driver; we are layering it on top of detection that already cannot be tampered with from the player's machine.",
  },
  {
    question: "What is a DMA cheat and why does it matter?",
    answer:
      "DMA stands for Direct Memory Access. It is a separate hardware device that reads game memory from outside the operating system, making it invisible to any software running on the player's computer, including kernel-level anti-cheat drivers. This is one of the reasons Ouro focuses on server-side detection, where the data is collected independently and cannot be interfered with from the client.",
  },
];

export default function AntiCheatPage() {
  const theme = useTheme();
  const sx = React.useMemo(() => getPublicSiteSx(theme), [theme]);

  return (
    <PublicPageLayout
      eyebrow="Anti-cheat"
      title="Server-side anti-cheat for serious CS2 matches."
      description={ANTI_CHEAT_PAGE_DESCRIPTION}
      mapBackground
    >
      <Box sx={sx.section}>
        <Box sx={sx.sectionHeader}>
          <Typography sx={sx.sectionTitle}>What Ouro collects</Typography>
          <Typography sx={sx.sectionCopy}>
            The anti-cheat watches the match from the server side and builds
            decisions from a wide set of gameplay signals. That includes timing,
            visibility, angles, shots, kills, and the sequence connecting them.
          </Typography>
        </Box>

        <Box sx={sx.featureHero}>
          <Box sx={sx.featureHeroCopy}>
            <Box sx={sx.cardIconWrap}>{collectionCards[0].icon}</Box>
            <Typography sx={{ ...sx.cardTitle, fontSize: "1.25rem" }}>
              {collectionCards[0].title}
            </Typography>
            <Typography sx={sx.cardBody}>{collectionCards[0].body}</Typography>
          </Box>
          <Box sx={sx.accentPanel}>
            <Typography sx={sx.cardTitle}>Server-owned evidence</Typography>
            <Typography sx={sx.cardBody}>
              The important anti-cheat signals come from the match server, not
              from clips, claims, or client-side data a cheater can shape around.
            </Typography>
          </Box>
        </Box>

        <Box sx={sx.featureScrollRow}>
          {collectionCards.slice(1).map((item) => (
            <Box key={item.title} sx={sx.featureScrollCard}>
              <Box sx={sx.cardIconWrap}>{item.icon}</Box>
              <Typography sx={sx.cardTitle}>{item.title}</Typography>
              <Typography sx={sx.cardBody}>{item.body}</Typography>
            </Box>
          ))}
        </Box>
      </Box>

      <Box sx={sx.section}>
        <Box sx={sx.sectionHeader}>
          <Typography sx={sx.sectionTitle}>What it can detect</Typography>
          <Typography sx={sx.sectionCopy}>
            Ouro can detect representative cheating patterns such as prefire
            angles that appear too consistent, hits through walls or before valid
            visibility, rapid trigger behavior, rapid kill sequences, and
            reaction-time or time-to-kill patterns that look too strong to be
            human. These examples are not the full list.
          </Typography>
        </Box>
        <Box sx={{ ...sx.cardGrid, gridTemplateColumns: { xs: "1fr", lg: "1.15fr 0.85fr" } }}>
          <Box sx={sx.card}>
            <Typography sx={sx.cardTitle}>Suspicious behavior categories</Typography>
            <Box component="ul" sx={sx.bulletList}>
              <li>
                <Typography component="span" sx={sx.listItemText}>
                  Prefire angles that repeat too cleanly and too often.
                </Typography>
              </li>
              <li>
                <Typography component="span" sx={sx.listItemText}>
                  Damage or hits that happen through walls or before proper visibility.
                </Typography>
              </li>
              <li>
                <Typography component="span" sx={sx.listItemText}>
                  Trigger behavior that is unnaturally fast or mechanically consistent.
                </Typography>
              </li>
              <li>
                <Typography component="span" sx={sx.listItemText}>
                  Kill chains and time-to-kill reactions that do not look natural.
                </Typography>
              </li>
              <li>
                <Typography component="span" sx={sx.listItemText}>
                  Repeated outlier patterns that stand apart from normal established play.
                </Typography>
              </li>
            </Box>
          </Box>
          <Box sx={sx.accentPanel}>
            <Typography sx={sx.cardTitle}>Why we do not publish every pattern</Typography>
            <Typography sx={sx.cardBody}>
              Players should understand that Ouro tracks a vast amount of data to
              support fair-play decisions. At the same time, we do not publish the
              full pattern library or decision logic because doing that would make
              it easier for cheaters to study around the system.
            </Typography>
          </Box>
        </Box>
      </Box>

      <Box sx={sx.section}>
        <Box sx={{ ...sx.cardGrid, gridTemplateColumns: { xs: "1fr", lg: "0.95fr 1.05fr" } }}>
          <Box sx={sx.accentPanel}>
            <Typography sx={sx.cardTitle}>Why server-side matters</Typography>
            <Typography sx={sx.cardBody}>
              Clips and reports only show fragments. Server-side analysis sees the
              whole engagement timeline tick by tick, which means Ouro can judge
              suspicious behavior with more context than a single moment ever can.
            </Typography>
          </Box>
          <Box sx={sx.card}>
            <Typography sx={sx.cardTitle}>How the system evolves</Typography>
            <Typography sx={sx.cardBody}>
              Ouro is constantly adding and testing new patterns as more gameplay
              data flows through the system. Long-established, legitimate players
              help strengthen the comparison baseline over time, which means the
              anti-cheat gets better at separating high-level legitimate play from
              suspicious outlier behavior.
            </Typography>
          </Box>
        </Box>
      </Box>

      <Box sx={sx.section}>
        <Box sx={sx.sectionHeader}>
          <Typography sx={sx.sectionTitle}>Server-side by design.</Typography>
          <Typography sx={sx.sectionCopy}>
            Most competitive platforms lean on a client-side anti-cheat that
            installs on the player's computer and tries to detect cheat software
            before it runs. Ouro inverts the priority on purpose: server-side
            analysis is the foundation, and a complementary client-side layer
            is being added to catch what server-side structurally cannot see.
          </Typography>
        </Box>
        <Box sx={{ ...sx.cardGrid, gridTemplateColumns: { xs: "1fr", lg: "1.1fr 0.9fr" } }}>
          <Box sx={sx.card}>
            <Typography sx={sx.cardTitle}>
              The arms race problem
            </Typography>
            <Typography sx={sx.cardBody}>
              Client-side anti-cheats, including kernel-level drivers that run
              with the deepest access on your operating system, create a
              constant arms race. Cheat developers reverse-engineer the
              detection, publish an update, and the cycle repeats. Even the
              largest platforms with dedicated security teams see their
              client-side protections bypassed regularly.
            </Typography>
            <Typography sx={{ ...sx.cardBody, mt: 1.5 }}>
              At the extreme end, hardware-based cheats known as DMA
              (Direct Memory Access) devices bypass client-side detection
              entirely. A DMA device is a separate piece of hardware, often
              plugged into a PCIe slot or connected via Thunderbolt, that reads
              the game's memory from outside the operating system. Because the
              reads happen below the OS layer, no software running on the
              player's machine, not even a kernel driver, can reliably see
              them. These setups are expensive, but for a motivated cheater
              they make client-side anti-cheat irrelevant.
            </Typography>
          </Box>
          <Box sx={sx.accentPanel}>
            <Typography sx={sx.cardTitle}>
              Why Ouro chose server-side
            </Typography>
            <Typography sx={sx.cardBody}>
              Server-side analysis cannot be tampered with from the player's
              machine. It does not matter what software or hardware a cheater
              uses locally because the server collects its own data
              independently. Combined with identity verification that makes
              bans meaningful instead of temporary, and a reputation system
              that tracks behavior over time, the result is a layered defense
              where each part compensates for the others.
            </Typography>
            <Typography sx={{ ...sx.cardBody, mt: 1.5 }}>
              A complementary client-side layer is in active development and
              is on the near-term roadmap. It is scoped to cover specific
              checks server-side analysis structurally cannot see — not to
              replace the foundation. Server-side detection and
              identity-backed consequences stay primary, because that is where
              the structural advantage lives.
            </Typography>
          </Box>
        </Box>
      </Box>

      <Box sx={sx.sectionWithMapBg1}>
        <Box sx={sx.sectionHeader}>
          <Typography sx={sx.sectionTitle}>How action works</Typography>
          <Typography sx={sx.sectionCopy}>
            The public version is simple on purpose: live server data is collected,
            suspicious patterns are flagged, full context is reviewed, and action
            follows when the evidence is strong enough.
          </Typography>
        </Box>
        <Box sx={{ ...sx.stepGrid, gridTemplateColumns: { xs: "1fr", md: "repeat(4, 1fr)" } }}>
          {actionSteps.map((step, index) => (
            <Box key={step.title} sx={sx.stepCard}>
              <Box sx={sx.stepHeader}>
                <Typography component="span" sx={sx.stepNumberInline}>
                  {index + 1}
                </Typography>
              </Box>
              <Typography sx={sx.stepTitle}>{step.title}</Typography>
              <Typography sx={sx.stepBody}>{step.body}</Typography>
            </Box>
          ))}
        </Box>
      </Box>

      <Box sx={sx.section}>
        <Box sx={sx.sectionHeader}>
          <Typography sx={sx.sectionTitle}>FAQ</Typography>
        </Box>
        <Box sx={sx.faqList}>
          {faqItems.map((item) => (
            <Box key={item.question} sx={sx.faqCard}>
              <Typography sx={sx.faqQuestion}>{item.question}</Typography>
              <Typography sx={sx.faqAnswer}>{item.answer}</Typography>
            </Box>
          ))}
        </Box>
      </Box>

      <Box sx={sx.callout}>
        <Typography sx={sx.sectionCopy}>
          Anti-cheat is one part of the broader fair-play system around Ouro.
          Explore how trust, safety, and platform standards connect together.
        </Typography>
        <Box
          sx={{
            mt: 3,
            display: "flex",
            justifyContent: "center",
            flexWrap: "wrap",
            gap: 1.5,
          }}
        >
          <Button component={Link} to="/trust-and-safety" variant="contained" sx={sx.primaryButton}>
            See trust and safety
          </Button>
          <Button component={Link} to="/faq" variant="outlined" sx={sx.secondaryButton}>
            Read the FAQ
          </Button>
        </Box>
      </Box>
    </PublicPageLayout>
  );
}
